import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Card,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  DialogTitle,
  Button,
  Dialog,
  DialogContent,
  Badge,
  Fade,
  Tooltip,
  TextField,
} from "@mui/material";
import {
  Warning,
  Visibility,
  VisibilityOff,
  CheckRounded as CheckRoundedIcon,
  AccessTime as AccessTimeIcon,
  ZoomIn,
  ZoomOut,
} from "@mui/icons-material";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MockMcq from "./MockMcq";
import MockNoteCompletion from "./MockNoteCompletion";
import TextHighlighterWrapper from "./TextHighlighterWrapper";
import NotesModal from "./NotesModal";
import HtmlRenderer from "./HtmlRenderer";

const DEFAULT_PART_TIMES = { partA: 15 * 60, BC_COMBINED: 45 * 60 }; // seconds

/* ---------------------------------------------------------------
   Header Toggle Icon Component
--------------------------------------------------------------- */

const HeaderToggleIcon = ({ isHeaderVisible, onToggle }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
        py: 1,
        bgcolor: "rgba(247, 247, 247, 0.95)",
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(157, 34, 53, 0.1)",
        position: "sticky",
        top: 0,
        zIndex: 999,
      }}
    >
      <Tooltip
        title={isHeaderVisible ? "Hide Header" : "Show Header"}
        placement="bottom"
        arrow
      >
        <IconButton
          onClick={onToggle}
          sx={{
            background: "linear-gradient(135deg, #9D2235 0%, #7a182a 100%)",
            color: "white",
            width: 40,
            height: 40,
            boxShadow: "0px 4px 16px rgba(157, 34, 53, 0.3)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              background: "linear-gradient(135deg, #7a182a 0%, #5d1220 100%)",
              transform: "translateY(-1px)",
              boxShadow: "0px 6px 20px rgba(157, 34, 53, 0.4)",
            },
            "&:active": {
              transform: "translateY(0px)",
            },
          }}
        >
          {isHeaderVisible ? (
            <VisibilityOff fontSize="small" />
          ) : (
            <Visibility fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Helper function to get all Part B question IDs for navigation chips (like ListeningSection)
function getPartBQuestionIds(readingData, rawReadingData = null) {
  // First try to get from raw data if available
  if (rawReadingData?.data?.readingTest?.oetReadingPart) {
    const partBData = rawReadingData.data.readingTest.oetReadingPart.find(
      (part) => part.part === "B"
    );
    if (partBData?.questions) {
      const questionIds = partBData.questions
        .map((q) => q.questionId)
        .sort((a, b) => a - b);
      return questionIds;
    }
  }

  // Check OET structure in main readingData
  if (readingData?.oetReadingPart) {
    const partBData = readingData.oetReadingPart.find(
      (part) => part.part === "B"
    );
    if (partBData?.questions) {
      const questionIds = partBData.questions
        .map((q) => q.questionId)
        .sort((a, b) => a - b);
      return questionIds;
    }
  }

  // Check standard structure
  if (readingData?.partB) {
    const allQuestionIds = [];
    if (readingData.partB.mcqQuestions) {
      allQuestionIds.push(
        ...readingData.partB.mcqQuestions.map((q) => q.question_id)
      );
    }
    if (readingData.partB.sentenceCompletionQuestions) {
      allQuestionIds.push(
        ...readingData.partB.sentenceCompletionQuestions.map(
          (q) => q.question_id
        )
      );
    }
    if (readingData.partB.noteCompletionQuestions) {
      allQuestionIds.push(
        ...readingData.partB.noteCompletionQuestions.map((q) => q.question_id)
      );
    }
    if (allQuestionIds.length > 0) {
      const sortedIds = allQuestionIds.sort(
        (a, b) => (parseInt(a) || 0) - (parseInt(b) || 0)
      );
      return sortedIds;
    }
  }

  // Default fallback: Return empty array if no data found
  return [];
}

/* ---------------------------------------------------------------
   Enhanced Gradient Countdown (Ring + Pie + Center Icon)
--------------------------------------------------------------- */
const EnhancedCountdown = ({
  size = 56,
  stroke = 5,
  progress = 0, // 0..100
  showPie = true, // show inside pie fill
  showRing = true, // show outer progress ring
  idSuffix = "reading",
}) => {
  const p = Math.min(100, Math.max(0, progress));
  const finished = p >= 100;

  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c;
  const offset = c * (1 - p / 100);

  // ------- PIE PATH (sector fill) -------
  const angle = (p / 100) * Math.PI * 2;
  const cx = size / 2;
  const cy = size / 2;
  const x = cx + r * Math.cos(-Math.PI / 2 + angle);
  const y = cy + r * Math.sin(-Math.PI / 2 + angle);
  const largeArc = angle > Math.PI ? 1 : 0;
  const piePath =
    p <= 0
      ? ""
      : `
    M ${cx} ${cy}
    L ${cx} ${cy - r}
    A ${r} ${r} 0 ${largeArc} 1 ${x} ${y}
    Z
  `;

  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id={`grad-${idSuffix}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#9D2235" />
            <stop offset="100%" stopColor="#5d1220" />
          </linearGradient>
          <radialGradient id={`soft-${idSuffix}`} cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="rgba(157,34,53,0.10)" />
            <stop offset="100%" stopColor="rgba(157,34,53,0.0)" />
          </radialGradient>
        </defs>

        {/* Soft background plate for a card-ish look */}
        <circle cx={cx} cy={cy} r={r} fill={`url(#soft-${idSuffix})`} />

        {/* Track */}
        {showRing && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="rgba(157,34,53,0.18)"
            strokeWidth={stroke}
          />
        )}

        {/* Pie fill from center (sector) */}
        {showPie && p > 0 && (
          <path d={piePath} fill={`url(#grad-${idSuffix})`} />
        )}

        {/* Progress Ring on top */}
        {showRing && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={`url(#grad-${idSuffix})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${dash}`}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        )}

        {/* Subtle glow when finished */}
        {finished && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="#9D2235"
            strokeOpacity="0.25"
            strokeWidth={2}
          />
        )}
      </svg>

      {/* Center icon */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "transform 180ms ease",
          transform: finished ? "scale(1.05)" : "scale(1)",
        }}
      >
        {finished ? (
          <CheckRoundedIcon
            sx={{ fontSize: Math.round(size * 0.34), color: "#7a182a" }}
          />
        ) : (
          <AccessTimeIcon
            sx={{ fontSize: Math.round(size * 0.34), color: "#7a182a" }}
          />
        )}
      </Box>
    </Box>
  );
};

export default function ReadingSection({
  readingData,
  rawReadingData, // Raw data for navigation chips
  answers,
  onAnswerChange,
  onAutoNext,
  instrOpen,
  timer = undefined, // minutes or "no-limit"
  mode = "simulation", // "practice" | "simulation"
  currentPart = "A", // "A" | "B" | "C"
  currentExtractIndex = 0, // 0‑based
  totalExtracts = 0, // supplied for part B, else 1,
  instructionsViewed = false, // Whether instructions have been viewed
  startTime = null, // ISO string from API
  endTime = null, // ISO string from API
  onBack = null, // Function to handle back navigation
  isLastQuestion = false, // Whether this is the last question in practice mode
  isLastModuleInSimulation = false, // Whether this is the last module in simulation mode (no writing/speaking after)
  isLastQuestionInSimulation = false, // Whether this is the last question/extract in simulation mode
  onFinish = null, // Function to handle finish action
  selectedParts = null, // Array of selected parts in practice mode for bullet display
  setCurrentPart: setCurrentPartProp, // for parent-state updates
  setCurrentExtractIndex: setCurrentExtractIndexProp, // for parent-state updates
  setAnswers: setAnswersProp, // for parent-state updates
  testId = null, // Test ID for browser back navigation
  bundleId = null, // Bundle ID for browser back navigation (if in bundle mode)
  // Notes props - now managed by parent component (same as ListeningSection)
  notes = [],
  onAddNote = null,
  notesModalOpen = false,
  setNotesModalOpen = null,
  onJumpToExtract = null,
}) {
  // ----- part data -----
  // For IELTS, currentPart is a section ID (long string), use currentExtractIndex to determine part
  // For OET, currentPart is "A", "B", or "C"
  const isIeltsSection = currentPart && currentPart.length > 5; // IELTS section IDs are long

  const partKey = useMemo(() => {
    if (isIeltsSection) {
      // For IELTS, map currentExtractIndex to part: 0=A, 1=B, 2=C, etc.
      const partLetter = String.fromCharCode(65 + currentExtractIndex); // 65 = 'A'
      return `part${partLetter}`;
    } else {
      // For OET, currentPart is already "A", "B", or "C"
      return `part${currentPart}`;
    }
  }, [isIeltsSection, currentExtractIndex, currentPart]);

  const part = useMemo(
    () => readingData?.[partKey] ?? {},
    [readingData, partKey]
  );
  const extracts = part.readingTexts ?? [];

  // For passages that include multiple short adverts/listings, split into cards
  // Example input has A\nHELP ... \n\nB\nGranta Hotel ...
  const splitAdBlocks = (paragraph) => {
    if (!paragraph || typeof paragraph !== "string") return [paragraph];
    // Split into chunks on 1+ blank lines
    const chunks = paragraph
      .split(/\n\s*\n+/)
      .map((c) => c.trim())
      .filter(Boolean);
    if (chunks.length <= 1) return [paragraph];

    // Parse label (single letter) if present at start of chunk
    return chunks.map((chunk) => {
      const lines = chunk
        .split(/\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      let label = null;
      let content = chunk;
      if (lines.length > 0 && /^[A-Z]$/.test(lines[0])) {
        label = lines[0];
        content = lines.slice(1).join("\n");
      }
      return { label, content };
    });
  };

  // Compute a readable part name. For IELTS this comes from the raw reading parts
  // (rawReadingData.data.readingTest.readingSection[*].readingParts[*].partName).
  // For OET or when not available, use the simple currentPart value (A/B/C)
  const partName = useMemo(() => {
    // IELTS structure: rawReadingData contains readingSection[] with readingParts
    try {
      const sections =
        rawReadingData?.data?.readingTest?.readingSection ||
        rawReadingData?.readingSection;
      if (sections && Array.isArray(sections)) {
        const currentSection = sections.find(
          (s) => s._id === currentPart || s.sectionName === currentPart
        );
        if (currentSection && Array.isArray(currentSection.readingParts)) {
          const partObj = currentSection.readingParts[currentExtractIndex];
          if (partObj && partObj.partName) return partObj.partName;
        }
      }
    } catch (err) {
      // Ignore and fallback
    }
    // OET fallback or any other fallback: return currentPart (should be A/B/C)
    return currentPart;
  }, [rawReadingData, currentPart, currentExtractIndex]);

  // Navigation hook
  const navigate = useNavigate();
  const location = useLocation();

  // Handle browser back button - navigate to main screen
  useEffect(() => {
    if (!testId) return; // Only if testId is provided

    const handlePopState = (event) => {
      // Prevent default back navigation
      event.preventDefault();
      // Detect test type from URL
      const path = location.pathname.toLowerCase();
      let backPath;
      if (bundleId) {
        backPath = `/app/bundles/${bundleId}`;
      } else if (path.includes('/mini-mock-test/')) {
        backPath = `/mini-mock-test/${testId}`;
      } else if (path.includes('/class-test/')) {
        backPath = `/class-test/${testId}`;
      } else {
        backPath = `/mock-test/${testId}`;
      }
      navigate(backPath, { replace: true });
    };

    // Add popstate event listener
    window.addEventListener("popstate", handlePopState);

    // Push current state to allow back button to trigger our handler
    window.history.pushState(null, "", window.location.pathname);

    // Cleanup
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [testId, bundleId, navigate]);

  // ----- Timer logic -----
  const customLimit =
    mode === "practice" && timer && timer !== "no-limit"
      ? Number(timer) * 60
      : null;

  const initSecs =
    customLimit ??
    (currentPart === "A"
      ? DEFAULT_PART_TIMES.partA
      : DEFAULT_PART_TIMES.BC_COMBINED);

  const [remaining, setRemaining] = useState(initSecs);
  const tickRef = useRef(null);
  const startedRef = useRef(false);
  const prevPartRef = useRef(currentPart);

  // Timer expired (part-level)
  const [timerExpired, setTimerExpired] = useState(false);
  const [expiryCountdown, setExpiryCountdown] = useState(10);
  const [expiryHandled, setExpiryHandled] = useState(false);
  const [stayHereUsed, setStayHereUsed] = useState(false);
  const [showExtendedTimer, setShowExtendedTimer] = useState(false);
  const [extendedCountdown, setExtendedCountdown] = useState(10);
  const extendedTimerRef = useRef(null);

  // For API-timer (backend start/end)
  const [apiTimeRemaining, setApiTimeRemaining] = useState(0);
  const [timerPaused, setTimerPaused] = useState(false);

  // Handle timer expiry - in practice mode, submit and show result, don't move; in simulation, move as before
  const handleTimerExpire = useCallback(() => {
    if (!expiryHandled) {
      setExpiryHandled(true);
      setTimerExpired(false);
      if (mode === "practice") {
        if (onFinish) onFinish(true);
      } else {
        if (isLastQuestion && onFinish) {
          onFinish();
        } else if (onAutoNext) {
          onAutoNext();
        }
      }
    }
  }, [expiryHandled, mode, isLastQuestion, onFinish, onAutoNext]);

  // Timer expiry effect: start countdown and auto-advance unless user clicks Continue
  useEffect(() => {
    if (timerExpired && !expiryHandled) {
      setExpiryCountdown(10);
      const interval = setInterval(() => {
        setExpiryCountdown((s) => {
          if (s <= 1) {
            clearInterval(interval);
            handleTimerExpire();
            return 10;
          }
          return s - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timerExpired, expiryHandled, handleTimerExpire]);

  // "Continue" button handler for timer modal
  const handleTimerContinue = () => {
    if (!expiryHandled) {
      handleTimerExpire();
    }
  };

  // "Stay Here" button handler for timer modal (simulation mode only)
  const handleStayHere = useCallback(() => {
    console.log("Stay Here clicked - starting 10 second auto-advance timer");

    // Close the modal and set flags
    setTimerExpired(false);
    setExpiryHandled(true);
    setStayHereUsed(true); // Mark that Stay Here has been used
    setShowExtendedTimer(true);
    setExtendedCountdown(10);

    console.log("Modal closed, timer states set, stayHereUsed = true");

    // Clear any existing timer
    if (extendedTimerRef.current) {
      clearInterval(extendedTimerRef.current);
      extendedTimerRef.current = null;
    }

    console.log("Starting 10 second countdown for auto-advance...");

    // Start 10 second countdown
    let count = 10;
    extendedTimerRef.current = setInterval(() => {
      count--;
      setExtendedCountdown(count);
      console.log("Extended countdown:", count);

      if (count <= 0) {
        console.log("Countdown finished - auto-advancing to next section");
        clearInterval(extendedTimerRef.current);
        extendedTimerRef.current = null;
        setShowExtendedTimer(false);
        
        // Auto-advance without showing modal again
        console.log("Extended timer finished - moving to next section automatically");
        if (mode === "simulation") {
          handleTimerExpire();
        }
      }
    }, 1000);

    console.log("Extended timer setup complete");
  }, [mode, handleTimerExpire]);

  // Reset expiryHandled for new part (not every extract)
  useEffect(() => {
    setExpiryHandled(false);
    setTimerExpired(false);
    setStayHereUsed(false);
    setShowExtendedTimer(false);
    setExtendedCountdown(10);
    
    // Clear extended timer if running
    if (extendedTimerRef.current) {
      clearInterval(extendedTimerRef.current);
      extendedTimerRef.current = null;
    }
  }, [currentPart]);

  // Track the time when instructions are opened
  const [instructionOpenTime, setInstructionOpenTime] = useState(null);
  const [totalInstructionTime, setTotalInstructionTime] = useState(0);

  // Check for extract change when component mounts or updates
  useEffect(() => {
    const extractChangeData = window.sessionStorage.getItem(
      "reading-extract-changing"
    );
    if (extractChangeData) {
      // Extract change is happening, but we don't need to track state for now
    }
  }, [instrOpen]);

  // Detect when instructions are opened and closed
  useEffect(() => {
    // When instructions are opened
    if (instrOpen && !instructionOpenTime) {
      setInstructionOpenTime(Date.now());
    }
    // When instructions are closed
    else if (!instrOpen && instructionOpenTime) {
      const timeSpentOnInstructions = Math.floor(
        (Date.now() - instructionOpenTime) / 1000
      );

      // Add to running total of instruction time
      const newTotal = totalInstructionTime + timeSpentOnInstructions;
      setTotalInstructionTime(newTotal);

      // Get any extract change data that might be relevant
      const extractChangeData = window.sessionStorage.getItem(
        "reading-extract-changing"
      );
      const isChangingExtract = !!extractChangeData;

      // If we have an end time, adjust it to compensate for instruction time
      if (endTime) {
        // Adjust by the time spent in this instruction modal
        // For extract changes, we need to add at least 10 seconds to avoid the timer issue
        const compensationTime = isChangingExtract
          ? Math.max(10, timeSpentOnInstructions) * 1000
          : timeSpentOnInstructions * 1000;

        const adjustedEndTime = new Date(
          new Date(endTime).getTime() + compensationTime
        );

        // We need to communicate this adjusted end time to parent component
        window.dispatchEvent(
          new CustomEvent("endTimeAdjusted", {
            detail: {
              adjustedEndTime: adjustedEndTime.toISOString(),
              timeSpentOnInstructions,
              totalInstructionTime: newTotal,
              isExtractChange: isChangingExtract,
            },
          })
        );

        // Clear extract change data from session storage
        if (isChangingExtract) {
          window.sessionStorage.removeItem("reading-extract-changing");
        }
      }

      setInstructionOpenTime(null);
    }
  }, [instrOpen, instructionOpenTime, endTime, totalInstructionTime]); // Main timer or API-timer watcher (part-level only)
  useEffect(() => {
    // Don't start timer if instructions haven't been viewed yet or are currently open
    if (!instructionsViewed || instrOpen) {
      return;
    }

    if (customLimit && !startedRef.current) {
      startedRef.current = true;
      clearInterval(tickRef.current);
      tickRef.current = setInterval(() => {
        setRemaining((r) => {
          if (r <= 1) {
            clearInterval(tickRef.current);
            tickRef.current = null;
            setTimerExpired(true);
            setExpiryHandled(false);
            return 0;
          }
          return r - 1;
        });
      }, 1000);
      setRemaining(customLimit);
    }

    if (!customLimit) {
      const secs =
        currentPart === "A"
          ? DEFAULT_PART_TIMES.partA
          : DEFAULT_PART_TIMES.BC_COMBINED;

      if (!tickRef.current || currentPart !== prevPartRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = setInterval(() => {
          setRemaining((r) => {
            if (r <= 1) {
              clearInterval(tickRef.current);
              tickRef.current = null;
              setTimerExpired(true);
              setExpiryHandled(false);
              return 0;
            }
            return r - 1;
          });
        }, 1000);
        setRemaining(secs);
      }
      prevPartRef.current = currentPart;
      return () => clearInterval(tickRef.current);
    }
  }, [customLimit, instrOpen, currentPart, instructionsViewed]);

  // API timer (from backend-provided start/end)
  useEffect(() => {
    // Don't start API timer if instructions haven't been viewed yet or are currently open
    if (!instructionsViewed || instrOpen) {
      return;
    }

    if (!endTime) return;

    // We'll use the endTime that was possibly adjusted by the instruction time compensation

    // Check if we need to adjust the timer due to instruction time
    const endDateTime = new Date(endTime);

    // We can add debug info to log the actual remaining time
    const now = new Date();
    const initialRemainingMs = endDateTime.getTime() - now.getTime();

    const updateApiTimer = () => {
      if (timerPaused) return;
      const now = new Date();
      const endDateTime = new Date(endTime);
      const remainingMs = endDateTime.getTime() - now.getTime();
      const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));

      // Update the timer display
      setApiTimeRemaining(remainingSeconds);

      // Check for timer expiration
      if (remainingSeconds <= 0 && !timerExpired && !expiryHandled) {
        if (mode === "simulation" && stayHereUsed) {
          // If Stay Here was already used, auto-advance without showing modal
          setExpiryHandled(true);
          handleTimerExpire();
        } else {
          // Show modal (in simulation mode, include Stay Here option)
          setTimerExpired(true);
          setExpiryHandled(false);
        }
      }
    };
    updateApiTimer();
    const interval = setInterval(updateApiTimer, 1000);

    const handlePauseTimer = () => {
      setTimerPaused(true);
    };
    window.addEventListener("pauseTestTimers", handlePauseTimer);

    return () => {
      clearInterval(interval);
      window.removeEventListener("pauseTestTimers", handlePauseTimer);
    };
  }, [
    endTime,
    timerPaused,
    timerExpired,
    expiryHandled,
    instructionsViewed,
    instrOpen,
    mode,
    stayHereUsed,
    handleTimerExpire,
  ]);

  // Calculate time for display
  let displayMins, displaySecs;
  const hasApiTimer = !!endTime;
  const isNoLimit = mode === "practice" && timer === "no-limit" && !hasApiTimer;

  if (isNoLimit) {
    displayMins = "--";
    displaySecs = "--";
  } else {
    const endTimeFromProps = endTime;
    if (endTimeFromProps) {
      displayMins = String(Math.floor(apiTimeRemaining / 60)).padStart(2, "0");
      displaySecs = String(apiTimeRemaining % 60).padStart(2, "0");
    } else {
      displayMins = String(Math.floor(remaining / 60)).padStart(2, "0");
      displaySecs = String(remaining % 60).padStart(2, "0");
    }
  }
  const mins = displayMins;
  const secs = displaySecs;

  // ----- Timer progress calculation -----
  const getTimerProgress = () => {
    if (isNoLimit) return 0;

    const endTimeFromProps = endTime;
    if (endTimeFromProps) {
      // API-based timer
      if (startTime) {
        const totalSeconds = Math.max(
          1,
          Math.floor(
            (new Date(endTimeFromProps).getTime() -
              new Date(startTime).getTime()) /
              1000
          )
        );
        const elapsedSeconds = totalSeconds - apiTimeRemaining;
        return Math.min(
          100,
          Math.max(0, (elapsedSeconds / totalSeconds) * 100)
        );
      }
      return 0;
    } else {
      // Local timer
      const totalSeconds = initSecs;
      const elapsedSeconds = totalSeconds - remaining;
      return Math.min(100, Math.max(0, (elapsedSeconds / totalSeconds) * 100));
    }
  };

  const timerProgress = getTimerProgress();

  // ----- bullet nav data -----
  const chipStates = useMemo(() => {
    let labels = [];
    let questionIds = [];

    // Check if this is IELTS structure (currentPart is a section ID, not a simple letter)
    const isIeltsSection = currentPart && currentPart.length > 5;

    if (isIeltsSection) {
      // For IELTS, check if this is a single-part section or multi-part section
      const allQuestions = part.questions || [];

      console.log("🔍 Debug allQuestions:", allQuestions.map(q => ({ 
        questionId: q.questionId, 
        question_id: q.question_id,
        _id: q._id 
      })));

      // If we have many questions (like 13 in Section 3), show all of them
      // If we have few questions (like 3-4 in Sections 1 and 2 parts), show only current part's questions
      const isSinglePartSection = allQuestions.length > 8; // Arbitrary threshold

      if (isSinglePartSection) {
        // Single-part section: show all questions (like Section 3)
        if (allQuestions.length > 0) {
          // Keep all questions including ones with null questionId (headings for notes_completion)
          // Sort only questions that have a valid questionId
          const sortedQuestions = [...allQuestions].sort((a, b) => {
            const aId = a.questionId || a.question_id;
            const bId = b.questionId || b.question_id;
            // Put null questionIds at the end or in their original order
            if (aId == null && bId == null) return 0;
            if (aId == null) return 1;
            if (bId == null) return -1;
            return aId - bId;
          });

          // Extract questionIds for questions that have them (for navigation)
          questionIds = sortedQuestions
            .filter((q) => (q.questionId || q.question_id) != null)
            .map((q) => q.question_id || q.questionId);
          
          labels = sortedQuestions
            .filter((q) => (q.questionId || q.question_id) != null)
            .map((q) => q.questionId || q.question_id);
          
          console.log("🔍 Final labels:", labels);
          console.log("🔍 Final questionIds:", questionIds);
        }
      } else {
        // Multi-part section: show all questions including headings with null questionId
        if (allQuestions.length > 0) {
          // Sort questions, putting null questionIds in their original order
          const sortedQuestions = [...allQuestions].sort((a, b) => {
            const aId = a.questionId || a.question_id;
            const bId = b.questionId || b.question_id;
            // Keep null questionIds in original order relative to each other
            if (aId == null && bId == null) return 0;
            if (aId == null) return 1;
            if (bId == null) return -1;
            return aId - bId;
          });

          // Extract questionIds only for questions that have them (for navigation)
          questionIds = sortedQuestions
            .filter((q) => (q.questionId || q.question_id) != null)
            .map((q) => q.question_id || q.questionId);
          
          labels = sortedQuestions
            .filter((q) => (q.questionId || q.question_id) != null)
            .map((q) => q.questionId || q.question_id);
        }
      }

      console.log("IELTS Chip States Debug:", {
        currentPart,
        isSinglePartSection,
        totalQuestions: allQuestions.length,
        questionIds,
        labels,
      });
    } else {
      // Original OET logic
      if (currentPart === "A") {
        labels = Array.from({ length: 20 }, (_, i) => 1 + i);
        // For Part A, get the actual question IDs
        if (part.mcqQuestions) {
          questionIds = part.mcqQuestions.map((q) => q.question_id);
        }
      } else if (currentPart === "B") {
        // For Part B, show ALL questions from ALL extracts (not just current extract)
        const allPartBQuestionIds = getPartBQuestionIds(
          readingData,
          rawReadingData
        );
        questionIds = allPartBQuestionIds;
        labels = allPartBQuestionIds; // Use actual question IDs as labels for Part B
      } else {
        const start = 27 + currentExtractIndex * 8;
        labels = Array.from({ length: 8 }, (_, i) => start + i);
        // For Part C, get the actual question IDs
        if (part.mcqQuestions) {
          questionIds = part.mcqQuestions.map((q) => q.question_id);
        }
        if (part.sentenceCompletionQuestions) {
          questionIds.push(
            ...part.sentenceCompletionQuestions.map((q) => q.question_id)
          );
        }
        if (part.noteCompletionQuestions) {
          questionIds.push(
            ...part.noteCompletionQuestions.map((q) => q.question_id)
          );
        }
      }

      if (mode === "practice") {
        const existingQuestionIds = [];
        if (part.mcqQuestions) {
          part.mcqQuestions.forEach((q) =>
            existingQuestionIds.push(q.question_id)
          );
        }
        if (part.sentenceCompletionQuestions) {
          part.sentenceCompletionQuestions.forEach((q) =>
            existingQuestionIds.push(q.question_id)
          );
        }
        if (part.noteCompletionQuestions) {
          part.noteCompletionQuestions.forEach((q) =>
            existingQuestionIds.push(q.question_id)
          );
        }
        if (existingQuestionIds.length > 0 && currentPart !== "B") {
          // For Part A and C, use existing question IDs in practice mode
          labels = existingQuestionIds;
          questionIds = existingQuestionIds;
        } else if (currentPart === "B") {
          // For Part B, always show all questions regardless of practice mode
          const allPartBQuestionIds = getPartBQuestionIds(
            readingData,
            rawReadingData
          );
          questionIds = allPartBQuestionIds;
          labels = allPartBQuestionIds;
        }
      }
    }

    return labels.map((label, index) => ({
      label,
      questionId: questionIds[index] || label,
      answered: Boolean(answers?.[questionIds[index] || label]),
    }));
  }, [
    currentPart,
    currentExtractIndex,
    answers,
    mode,
    part,
    readingData,
    rawReadingData,
  ]);

  // Highlight helper - now using TextHighlighterWrapper

  // next button label
  const getButtonText = () => {
    // Check if this is the last question in simulation mode and reading is the last module
    if (
      mode === "simulation" &&
      isLastModuleInSimulation &&
      isLastQuestionInSimulation
    ) {
      return "Finish";
    }
    if (currentPart === "A") return "Next Part";
    if (currentPart === "B") {
      return currentExtractIndex < totalExtracts - 1
        ? "Next Extract"
        : "Next Part";
    }
    return currentExtractIndex < totalExtracts - 1 ? "Next Extract" : "Next Section";
  };

  // question base number
  const questionBase =
    currentPart === "A"
      ? 1
      : currentPart === "B"
      ? 21
      : 27 + currentExtractIndex * 8; // part C (two blocks of 8)

  // Navigation confirmation modal
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // Header visibility state
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  
  // Zoom level states for left and right panels
  const [leftZoom, setLeftZoom] = useState(100);
  const [rightZoom, setRightZoom] = useState(100);

  // Returns all question IDs for the current extract (MCQ, sentence, note)
  const getCurrentExtractQuestionIds = useCallback(() => {
    let ids = [];
    if (part.mcqQuestions?.length) {
      ids = ids.concat(part.mcqQuestions.map((q) => q.question_id));
    }
    if (part.sentenceCompletionQuestions?.length) {
      ids = ids.concat(
        part.sentenceCompletionQuestions.map((q) => q.question_id)
      );
    }
    if (part.noteCompletionQuestions?.length) {
      ids = ids.concat(part.noteCompletionQuestions.map((q) => q.question_id));
    }
    return ids;
  }, [part]);

  const handleNextClick = useCallback(() => {
    // 1) collect ONLY the current extract’s question IDs
    const ids = getCurrentExtractQuestionIds(); // this already exists above
    const anyUnattempted = ids.some((id) => {
      const v = answers?.[id];
      return v == null || String(v).trim() === "";
    });

    // 2) what to do after confirming (or when all answered)
    const proceed = () => {
      // Check if this is IELTS structure (currentPart is a section ID, not a simple letter)
      const isIeltsSection = currentPart && currentPart.length > 5; // IELTS section IDs are long

      // IELTS: Check if there are more extracts/parts within the current section
      if (isIeltsSection) {
        const isLastExtract = currentExtractIndex >= totalExtracts - 1;

        if (!isLastExtract) {
          console.log(
            `IELTS: Moving to next extract (${
              currentExtractIndex + 1
            }/${totalExtracts}) within section`
          );
          window.scrollTo({ top: 0, behavior: "smooth" });
          if (typeof onJumpToExtract === "function") {
            onJumpToExtract(currentPart, currentExtractIndex + 1); // Keep same section, next extract
          } else if (typeof setCurrentExtractIndexProp === "function") {
            // fallback for practice legacy
            setCurrentExtractIndexProp(currentExtractIndex + 1);
          }
          return;
        } else {
          console.log(
            "IELTS: Last extract in section, proceeding to next section"
          );
          // User clicked Next on last extract, proceed to next section
        }
      }
      // OET: Part B navigation (original logic)
      else if (currentPart === "B") {
        const allPartBQuestionIds = getPartBQuestionIds(
          readingData,
          rawReadingData
        );
        const isLastExtract =
          currentExtractIndex >= allPartBQuestionIds.length - 1;

        if (!isLastExtract) {
          window.scrollTo({ top: 0, behavior: "smooth" });
          if (typeof onJumpToExtract === "function") {
            onJumpToExtract("B", currentExtractIndex + 1); // works in both modes
          } else if (typeof setCurrentExtractIndexProp === "function") {
            // fallback for practice legacy
            setCurrentExtractIndexProp(currentExtractIndex + 1);
          }
          return;
        }
      }

      // Default: next part / finish
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (mode === "practice" && isLastQuestion && onFinish) {
        onFinish();
      } else if (
        mode === "simulation" &&
        isLastModuleInSimulation &&
        isLastQuestionInSimulation &&
        onFinish
      ) {
        // In simulation mode, if this is the last module and last question, call onFinish
        onFinish();
      } else {
        onAutoNext?.();
      }
    };
    // 3) show confirm if anything unattempted
    if (anyUnattempted) {
      setPendingAction(() => proceed);
      setShowConfirm(true);
    } else {
      proceed();
    }
  }, [
    answers,
    currentPart,
    currentExtractIndex,
    isLastQuestion,
    isLastModuleInSimulation,
    isLastQuestionInSimulation,
    mode,
    onAutoNext,
    onFinish,
    readingData,
    rawReadingData,
    onJumpToExtract, // <-- make sure this is in the deps
    setCurrentExtractIndexProp, // <-- fallback deps
    totalExtracts, // <-- added for IELTS navigation
  ]);

  const handleConfirmYes = () => {
    setShowConfirm(false);
    if (pendingAction) pendingAction();
  };
  const handleConfirmNo = () => {
    setShowConfirm(false);
    setPendingAction(null);
  };

  // Handle chip click to navigate to specific question
  const handleChipClick = (questionId) => {
    // --- Part B: jump to the extract that owns this question ---
    if (currentPart === "B") {
      const allPartBQuestionIds = getPartBQuestionIds(
        readingData,
        rawReadingData
      );
      const questionIndex = allPartBQuestionIds.findIndex(
        (id) => String(id) === String(questionId)
      );

      if (questionIndex !== -1) {
        // Smooth scroll to top first
        window.scrollTo({ top: 0, behavior: "smooth" });

        // Prefer parent callback (handles SIMULATION via navigate; PRACTICE via setState)
        if (typeof onJumpToExtract === "function") {
          onJumpToExtract("B", questionIndex);
        } else {
          // Fallback: practice-only local state (keeps answers intact)
          setCurrentPartProp?.("B");
          setCurrentExtractIndexProp?.(questionIndex);
        }

        // After navigation/render, try to scroll the exact question into view & highlight
        setTimeout(() => {
          const el = document.querySelector(
            `[data-question-id="${questionId}"]`
          );
          if (!el) {
            // If not ready yet, retry once a bit later
            setTimeout(() => {
              const el2 = document.querySelector(
                `[data-question-id="${questionId}"]`
              );
              if (el2) {
                el2.scrollIntoView({ behavior: "smooth", block: "center" });
              }
            }, 1000);
            return;
          }

          el.scrollIntoView({ behavior: "smooth", block: "center" });

          // Professional highlight pulse
          const originalBackground = el.style.backgroundColor;
          const originalTransition = el.style.transition;
          const originalBoxShadow = el.style.boxShadow;

          el.style.transition = "all 0.3s ease-in-out";
          el.style.backgroundColor = "rgba(157, 34, 53, 0.08)";
          el.style.boxShadow =
            "0 0 0 3px rgba(157, 34, 53, 0.2), 0 4px 12px rgba(157, 34, 53, 0.15)";
          el.style.borderRadius = "8px";

          setTimeout(() => {
            el.style.backgroundColor = "rgba(157, 34, 53, 0.15)";
            el.style.boxShadow =
              "0 0 0 4px rgba(157, 34, 53, 0.3), 0 6px 16px rgba(157, 34, 53, 0.2)";
          }, 150);

          setTimeout(() => {
            el.style.backgroundColor = "rgba(157, 34, 53, 0.05)";
            el.style.boxShadow =
              "0 0 0 2px rgba(157, 34, 53, 0.1), 0 2px 8px rgba(157, 34, 53, 0.1)";
          }, 400);

          setTimeout(() => {
            el.style.transition = originalTransition;
            el.style.backgroundColor = originalBackground;
            el.style.boxShadow = originalBoxShadow;
            el.style.borderRadius = "";
          }, 1000);
        }, 500);

        return;
      }
    }

    // --- Part A & C: just scroll to the question & highlight it ---
    const element = document.querySelector(
      `[data-question-id="${questionId}"]`
    );
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });

      const originalBackground = element.style.backgroundColor;
      const originalTransition = element.style.transition;
      const originalBoxShadow = element.style.boxShadow;

      element.style.transition = "all 0.3s ease-in-out";
      element.style.backgroundColor = "rgba(157, 34, 53, 0.08)";
      element.style.boxShadow =
        "0 0 0 3px rgba(157, 34, 53, 0.2), 0 4px 12px rgba(157, 34, 53, 0.15)";
      element.style.borderRadius = "8px";

      setTimeout(() => {
        element.style.backgroundColor = "rgba(157, 34, 53, 0.15)";
        element.style.boxShadow =
          "0 0 0 4px rgba(157, 34, 53, 0.3), 0 6px 16px rgba(157, 34, 53, 0.2)";
      }, 150);

      setTimeout(() => {
        element.style.backgroundColor = "rgba(157, 34, 53, 0.05)";
        element.style.boxShadow =
          "0 0 0 2px rgba(157, 34, 53, 0.1), 0 2px 8px rgba(157, 34, 53, 0.1)";
      }, 400);

      setTimeout(() => {
        element.style.transition = originalTransition;
        element.style.backgroundColor = originalBackground;
        element.style.boxShadow = originalBoxShadow;
        element.style.borderRadius = "";
      }, 1000);
    }
  };

  // Custom Back Button Logic for Part B
  // Back button logic: show on all extracts except first in B/C (any mode)
  // ---- Back button logic (shows in all modes except on first extract of B/C) ----
  const showBackButton = useMemo(() => {
    if (currentPart === "B" && currentExtractIndex > 0) return true;
    if (currentPart === "C" && currentExtractIndex >= 0) return true; // Show back button on Part C extract 1 to go back to Part B
    return false;
  }, [currentPart, currentExtractIndex]);

  const handleBackClick = () => {
    if (onBack) {
      // Special case: Part C extract 1 should go back to Part B
      if (currentPart === "C" && currentExtractIndex === 0) {
        onBack(-1, currentPart); // -1 indicates going back to previous part
      } else if (currentPart === "B" && currentExtractIndex > 0) {
        // Simulation mode: decrement extract index within Part B
        onBack(currentExtractIndex - 1, currentPart);
      } else {
        // Regular back navigation within the same part
        onBack(currentExtractIndex - 1, currentPart);
      }
    }
  };

  // --------------- B/C Extract Navigation UI -------------------
  // Only render if parent provided these setters (for parent state)
  const canJumpNav =
    !!setCurrentPartProp && !!setCurrentExtractIndexProp && !!setAnswersProp;

  // Get extract counts for B and C for navigation
  const partBExtractCount = readingData?.partB?.readingTexts?.length || 0;
  const partCExtractCount = readingData?.partC?.readingTexts?.length || 0;

  // =================== UI ===================
  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* ===== Header ===== */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          bgcolor: "#F7F7F7",
          px: { xs: 0.5, sm: 0.5, md: 0.5, lg: 0.5 },
          py: { xs: 0.1 },
          position: "sticky",
          top: 0,
          zIndex: 1000,
          display: isHeaderVisible ? "block" : "none",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Card
          elevation={1}
          sx={{
            width: "100%",
            mb: 1,
            p: { xs: 0.2, sm: 0.8, md: 1, lg: 1.2 },
            bgcolor: "#fff",
            borderRadius: { xs: 0.8, sm: 1.5, md: 2 },
            boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              alignItems: "center",
              gridTemplateAreas: {
                xs: `"left" "center" "right"`,
                sm: `"left center right"`,
                md: `"left center right"`,
                lg: `"left center right"`,
              },
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 2fr 1fr",
                md: "1fr 2fr 1fr",
                lg: "1fr 2fr 1fr",
              },
              gridTemplateRows: { xs: "auto auto auto", sm: "auto" },
              columnGap: { xs: 0, sm: 0.8, md: 1.2, lg: 1.5 },
              rowGap: { xs: 0.3, sm: 0 },
              minWidth: 0,
              minHeight: { xs: 20, sm: 45, md: 50 },
            }}
          >
            {/* LEFT - Reading Info */}
            <Box
              sx={{
                gridArea: "left",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
                gap: { xs: 0.3, sm: 1, md: 1.2, lg: 1.5 },
                py: { xs: 0.1, sm: 0.2 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  minWidth: 0,
                  flexShrink: 1,
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#9D2235",
                    fontSize: { xs: "0.6rem", sm: "0.85rem", md: "0.95rem" },
                    lineHeight: 1.1,
                    whiteSpace: "nowrap",
                  }}
                >
                  Reading Module
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#7a7a7a",
                    fontSize: { xs: "0.55rem", sm: "0.75rem", md: "0.8rem" },
                    lineHeight: 1.1,
                    letterSpacing: 0.1,
                    whiteSpace: "nowrap",
                  }}
                >
                  <b>
                    Part ({partName}) Reading Passage {currentExtractIndex + 1} of{" "}
                    {totalExtracts || extracts.length || 1}
                  </b>
                </Typography>
              </Box>
            </Box>

            {/* CENTER - Question Navigation Chips */}
            <Box
              sx={{
                gridArea: "center",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                gap: { xs: 0.2, sm: 0.8, md: 1.2, lg: 1.5 },
                minWidth: 0,
                justifyContent: "center",
                py: { xs: 0.1, sm: 0.3, md: 0.4 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  px: 0,
                  py: 0,
                  minWidth: { xs: "100%", sm: "auto" },
                  justifyContent: "center", // Changed from flex-start to center for all screen sizes
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    gap: { xs: 0.2, sm: 0.3, md: 0.4, lg: 0.5 },
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  {chipStates.map(({ label, questionId, answered }) => (
                    <Box
                      key={label}
                      onClick={() => handleChipClick(questionId)}
                      sx={{
                        width: { xs: 18, sm: 22, md: 26, lg: 30 },
                        height: { xs: 18, sm: 22, md: 26, lg: 30 },
                        borderRadius: "50%",
                        border: "1.5px solid #9D2235",
                        bgcolor: answered ? "#9D2235" : "transparent",
                        color: answered ? "#fff" : "#9D2235",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: {
                          xs: "0.6rem",
                          sm: "0.7rem",
                          md: "0.8rem",
                          lg: "0.9rem",
                        },
                        cursor: "pointer",
                        transition: "background 0.15s",
                        "&:hover": {
                          bgcolor: answered
                            ? "#7a182a"
                            : "rgba(157,34,53,0.08)",
                        },
                        "&:active": {},
                        flex: "0 0 auto",
                      }}
                    >
                      {label}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* RIGHT - Timer and Notes */}
            <Box
              sx={{
                gridArea: "right",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.3, sm: 0.6, md: 0.8 },
              }}
            >
              {/* Circle Clock - Above everything */}
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <EnhancedCountdown
                  size={20}
                  stroke={2}
                  progress={timerProgress}
                  showPie
                  showRing
                  idSuffix="readingTime"
                />
              </Box>

              {/* Timer and Notes Row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: { xs: 0.4, sm: 0.8, md: 1 },
                }}
              >
                {/* Timer */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 0.5, sm: 0.6, md: 0.7 },
                    px: { xs: 0.8, sm: 1, md: 1.2 },
                    py: { xs: 0.4, sm: 0.5, md: 0.6 },
                    borderRadius: { xs: 1.5, sm: 2, md: 2.5 },
                    minWidth: { xs: 70, sm: 90, md: 110 },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <AccessTimeIcon
                    sx={{
                      fontSize: { xs: 16, sm: 20, md: 24 },
                      color: "#9D2235",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: "0.8rem", sm: "1rem", md: "1.2rem" },
                      fontWeight: 800,
                      color: "#9D2235",
                      fontFamily: "'Roboto Mono', 'Courier New', monospace",
                      letterSpacing: { xs: 0.4, sm: 0.6, md: 0.8 },
                      lineHeight: 1,
                    }}
                  >
                    {mins}:{secs}
                  </Typography>
                </Box>

                {/* Notes Icon */}
                <Badge
                  badgeContent={notes?.length || 0}
                  sx={{
                    "& .MuiBadge-badge": {
                      bgcolor: "#fff",
                      color: "#9D2235",
                      fontWeight: "bold",
                      fontSize: { xs: "0.35rem", sm: "0.45rem", md: "0.5rem" },
                      minWidth: { xs: 6, sm: 10, md: 12 },
                      height: { xs: 6, sm: 10, md: 12 },
                      boxShadow: "0 2px 4px rgba(157,34,53,0.2)",
                    },
                  }}
                >
                  <IconButton
                    onClick={() => setNotesModalOpen(true)}
                    sx={{
                      background:
                        "linear-gradient(135deg, #9D2235 0%, #7a182a 100%)",
                      color: "#fff",
                      border: "1px solid rgba(255,255,255,0.1)",
                      boxShadow:
                        "0 4px 12px rgba(157,34,53,0.25), 0 2px 4px rgba(157,34,53,0.15)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #7a182a 0%, #5d1220 100%)",
                        transform: "translateY(-1px)",
                        boxShadow:
                          "0 6px 16px rgba(157,34,53,0.3), 0 4px 8px rgba(157,34,53,0.2)",
                      },
                      width: { xs: 18, sm: 28, md: 32 },
                      height: { xs: 18, sm: 28, md: 32 },
                    }}
                  >
                    <StickyNote2Icon
                      sx={{
                        fontSize: { xs: "0.6rem", sm: "0.9rem", md: "1rem" },
                        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
                      }}
                    />
                  </IconButton>
                </Badge>
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>
      {/* ===== Split‑screen ===== */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          overflow: "hidden",
          flexDirection: { xs: "column", md: "row" },
          pt: 0,
          transition: "padding-top 0.3s ease-in-out",
        }}
      >
        {/* left: passage */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            height: { xs: "50%", md: "100%" },
            p: { xs: 1, sm: 2 },
            overflow: "auto",
            borderRight: { md: "2px solid #e0e0e0" },
            borderBottom: { xs: "2px solid #e0e0e0", md: "none" },
            background: "#fff",
            position: "relative",
          }}
        >
          {/* Zoom Controls - Left Panel */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 0.5,
              mb: 2,
              bgcolor: "rgba(255,255,255,0.98)",
              backdropFilter: "blur(12px)",
              px: 1.5,
              py: 1,
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid rgba(157, 34, 53, 0.1)",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#64748b",
                fontWeight: 600,
                fontSize: "0.75rem",
                mr: 0.5,
              }}
            >
              Passage
            </Typography>
            <IconButton
              size="small"
              onClick={() => setLeftZoom(prev => Math.max(70, prev - 10))}
              disabled={leftZoom <= 70}
              sx={{
                width: 32,
                height: 32,
                bgcolor: leftZoom <= 70 ? "#f1f5f9" : "white",
                border: "1px solid #e2e8f0",
                "&:hover": {
                  bgcolor: leftZoom <= 70 ? "#f1f5f9" : "#9D2235",
                  color: "white",
                  borderColor: "#9D2235",
                },
                "&:disabled": {
                  opacity: 0.4,
                  cursor: "not-allowed",
                },
              }}
            >
              <ZoomOut sx={{ fontSize: 18 }} />
            </IconButton>
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                bgcolor: "#f8fafc",
                borderRadius: 1,
                minWidth: 55,
                textAlign: "center",
                fontSize: "0.813rem",
                fontWeight: 700,
                color: "#9D2235",
                border: "1px solid #e2e8f0",
              }}
            >
              {leftZoom}%
            </Box>
            <IconButton
              size="small"
              onClick={() => setLeftZoom(prev => Math.min(150, prev + 10))}
              disabled={leftZoom >= 150}
              sx={{
                width: 32,
                height: 32,
                bgcolor: leftZoom >= 150 ? "#f1f5f9" : "white",
                border: "1px solid #e2e8f0",
                "&:hover": {
                  bgcolor: leftZoom >= 150 ? "#f1f5f9" : "#9D2235",
                  color: "white",
                  borderColor: "#9D2235",
                },
                "&:disabled": {
                  opacity: 0.4,
                  cursor: "not-allowed",
                },
              }}
            >
              <ZoomIn sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <TextHighlighterWrapper onAddNote={onAddNote}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                mb: { xs: 1, sm: 2 },
                fontSize: `calc((${leftZoom / 100}) * ${window.innerWidth < 600 ? '1rem' : '1.25rem'})`,
              }}
            >
              Reading Passage
            </Typography>
            {extracts.map((txt, i) => (
              <Card
                key={i}
                elevation={2}
                sx={{
                  mb: { xs: 2, sm: 3 },
                  p: { xs: 1.5, sm: 3 },
                  borderRadius: 2,
                  boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                  background: "#fff",
                }}
              >
                {txt.title && (
                  <HtmlRenderer
                    content={txt.title}
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: { xs: 1.5, sm: 2 },
                      textAlign: "center",
                      color: "#222",
                      fontSize: `calc((${leftZoom / 100}) * ${window.innerWidth < 600 ? '1rem' : '1.25rem'})`,
                    }}
                  />
                )}
                {txt.paragraphs?.map((p, pIdx) => {
                  const adBlocks = splitAdBlocks(p);
                  if (adBlocks.length > 1) {
                    return (
                      <Box
                        key={pIdx}
                        sx={{
                          display: "grid",
                          // Force a single card per row; user requested 1 card per row
                          gridTemplateColumns: { xs: "1fr" },
                          gap: 2,
                          mb: { xs: 1.5, sm: 2 },
                        }}
                      >
                        {adBlocks.map((b, bi) => (
                          <Card
                            key={bi}
                            elevation={2}
                            sx={{ p: 2, borderRadius: 2, bgcolor: "#fff" }}
                          >
                            {b.label && (
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 800,
                                  color: "#9D2235",
                                  mb: 1,
                                }}
                              >
                                {b.label}
                              </Typography>
                            )}
                            <HtmlRenderer
                              content={b.content}
                              component="div"
                              sx={{ 
                                whiteSpace: "pre-line",
                                fontSize: `calc((${leftZoom / 100}) * ${window.innerWidth < 600 ? '0.875rem' : '1rem'})`,
                              }}
                            />
                          </Card>
                        ))}
                      </Box>
                    );
                  }

                  return (
                    <HtmlRenderer
                      key={pIdx}
                      content={p}
                      component="div"
                      sx={{
                        mb: { xs: 1.5, sm: 2 },
                        lineHeight: { xs: 1.6, sm: 1.8 },
                        whiteSpace: "pre-line",
                        textAlign: "justify",
                        fontSize: `calc((${leftZoom / 100}) * ${window.innerWidth < 600 ? '0.875rem' : '1rem'})`,
                      }}
                    />
                  );
                })}
                {txt.imageUrl && (
                  <Box textAlign="center" sx={{ mb: { xs: 1.5, sm: 2 } }}>
                    <img
                      src={`${txt.imageUrl}`}
                      alt={txt.title || "Reading image"}
                      style={{ maxWidth: "100%", height: "auto" }}
                    />
                  </Box>
                )}
                {txt.tables && (
                  <>
                    {txt.tables.mainHeading && (
                      <HtmlRenderer
                        content={txt.tables.mainHeading}
                        component="h6"
                        sx={{
                          mb: { xs: 1, sm: 1.5 },
                          fontWeight: 600,
                          fontSize: { xs: "1rem", sm: "1.125rem" },
                        }}
                      />
                    )}
                    {txt.tables.instructions && (
                      <HtmlRenderer
                        content={txt.tables.instructions}
                        component="div"
                        sx={{
                          mb: { xs: 1, sm: 1.5 },
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        }}
                      />
                    )}
                    <TableContainer
                      component={Paper}
                      sx={{
                        mb: { xs: 1.5, sm: 2 },
                        overflowX: "auto",
                      }}
                    >
                      <Table
                        size={window.innerWidth < 600 ? "small" : "medium"}
                      >
                        <TableHead>
                          <TableRow>
                            {txt.tables.columns?.map((h, hIdx) => (
                              <TableCell
                                key={hIdx}
                                sx={{
                                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                  fontWeight: 600,
                                }}
                              >
                                <HtmlRenderer
                                  content={h}
                                  component="span"
                                  sx={{
                                    fontSize: "inherit",
                                    fontWeight: "inherit",
                                  }}
                                />
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {txt.tables.rows?.map((row, rIdx) => (
                            <TableRow key={rIdx}>
                              {row.map((cell, cIdx) => (
                                <TableCell
                                  key={cIdx}
                                  sx={{
                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                  }}
                                >
                                  <HtmlRenderer
                                    content={cell}
                                    component="span"
                                    sx={{
                                      fontSize: "inherit",
                                    }}
                                  />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Card>
            ))}
          </TextHighlighterWrapper>
        </Box>
        {/* right: questions */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            height: { xs: "50%", md: "100%" },
            p: { xs: 1, sm: 2 },
            overflow: "auto",
            background: "#f8f9fa",
          }}
        >
          {/* Zoom Controls - Right Panel */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 0.5,
              mb: 2,
              bgcolor: "rgba(255,255,255,0.98)",
              backdropFilter: "blur(12px)",
              px: 1.5,
              py: 1,
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid rgba(157, 34, 53, 0.1)",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#64748b",
                fontWeight: 600,
                fontSize: "0.75rem",
                mr: 0.5,
              }}
            >
              Questions
            </Typography>
            <IconButton
              size="small"
              onClick={() => setRightZoom(prev => Math.max(70, prev - 10))}
              disabled={rightZoom <= 70}
              sx={{
                width: 32,
                height: 32,
                bgcolor: rightZoom <= 70 ? "#f1f5f9" : "white",
                border: "1px solid #e2e8f0",
                "&:hover": {
                  bgcolor: rightZoom <= 70 ? "#f1f5f9" : "#9D2235",
                  color: "white",
                  borderColor: "#9D2235",
                },
                "&:disabled": {
                  opacity: 0.4,
                  cursor: "not-allowed",
                },
              }}
            >
              <ZoomOut sx={{ fontSize: 18 }} />
            </IconButton>
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                bgcolor: "#f8fafc",
                borderRadius: 1,
                minWidth: 55,
                textAlign: "center",
                fontSize: "0.813rem",
                fontWeight: 700,
                color: "#9D2235",
                border: "1px solid #e2e8f0",
              }}
            >
              {rightZoom}%
            </Box>
            <IconButton
              size="small"
              onClick={() => setRightZoom(prev => Math.min(150, prev + 10))}
              disabled={rightZoom >= 150}
              sx={{
                width: 32,
                height: 32,
                bgcolor: rightZoom >= 150 ? "#f1f5f9" : "white",
                border: "1px solid #e2e8f0",
                "&:hover": {
                  bgcolor: rightZoom >= 150 ? "#f1f5f9" : "#9D2235",
                  color: "white",
                  borderColor: "#9D2235",
                },
                "&:disabled": {
                  opacity: 0.4,
                  cursor: "not-allowed",
                },
              }}
            >
              <ZoomIn sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <TextHighlighterWrapper onAddNote={onAddNote}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                mb: { xs: 1, sm: 2 },
                fontSize: `calc((${rightZoom / 100}) * ${window.innerWidth < 600 ? '1rem' : '1.25rem'})`,
              }}
            >
              Questions
            </Typography>
            {/* Render all questions in numerical order by questionId, grouped by instruction */}
            {(() => {
              // Get all questions - keep them in exact API order
              const allQuestions = part.questions || [];
              console.log("🔍 Initial part.questions:", allQuestions.length, allQuestions.map(q => ({
                _id: q._id,
                questionId: q.questionId,
                questionType: q.questionType,
                question: q.question?.substring(0, 50)
              })));
              
              // Don't sort - keep original API order to preserve heading positions
              const sortedQuestions = [...allQuestions];
              
              // Group mcq_multiple questions by questionRange while preserving original order
              const mcqMultipleByRange = new Map();
              const mcqMultipleFirstIndex = new Map(); // Track original index of first question in each group
              const allQuestionsWithIndex = []; // Store all questions with their original index
              
              sortedQuestions.forEach((q, idx) => {
                if (q.questionType === 'mcq_multiple') {
                  // Create a grouping key based on questionRange or question text + options
                  const rangeKey = q.questionRange || '';
                  const questionKey = `${q.question || ''}_${JSON.stringify(q.options || [])}`;
                  const groupKey = rangeKey || questionKey;
                  
                  if (!mcqMultipleByRange.has(groupKey)) {
                    mcqMultipleByRange.set(groupKey, []);
                    mcqMultipleFirstIndex.set(groupKey, idx); // Store original index of first in group
                  }
                  mcqMultipleByRange.get(groupKey).push({ question: q, originalIndex: idx });
                } else {
                  // Non-mcq_multiple questions keep their original index
                  allQuestionsWithIndex.push({ question: q, originalIndex: idx });
                }
              });
              
              // Add grouped mcq_multiple questions at their proper position (first question's index)
              mcqMultipleByRange.forEach((group, groupKey) => {
                const firstOriginalIdx = mcqMultipleFirstIndex.get(groupKey);
                
                if (group.length > 1) {
                  // Multiple questions with same range - create grouped question
                  const questions = group.map(g => g.question);
                  allQuestionsWithIndex.push({
                    originalIndex: firstOriginalIdx,
                    question: {
                      ...questions[0], // Use first question's data as base
                      questionIds: questions.map(q => q.questionId || q._id),
                      isGrouped: true,
                      groupedQuestions: questions,
                    }
                  });
                } else {
                  // Single question - add as normal
                  allQuestionsWithIndex.push({
                    originalIndex: firstOriginalIdx,
                    question: group[0].question,
                  });
                }
              });
              
              // Sort ALL questions by their original index to maintain proper order
              allQuestionsWithIndex.sort((a, b) => a.originalIndex - b.originalIndex);
              
              // Extract just the questions (without the index wrapper)
              const finalSortedQuestions = allQuestionsWithIndex.map(entry => entry.question);

              // Helper function to get best instruction for each question type
              // Prefer instructions with HTML formatting (like <b> tags) from first occurrence
              const getInstructionByType = (questionType) => {
                const sameTypeQuestions = allQuestions.filter(q => {
                  const qType = (q.questionType || "").toLowerCase();
                  const targetType = (questionType || "").toLowerCase();
                  return qType === targetType;
                });
                
                // Find first question with HTML-formatted instructions (contains < or >)
                const withHtml = sameTypeQuestions.find(q => 
                  q.instructions && (q.instructions.includes('<') || q.instructions.includes('>'))
                );
                
                if (withHtml) return withHtml.instructions;
                
                // Otherwise return first available instruction
                return sameTypeQuestions.find(q => q.instructions)?.instructions || "";
              };

              console.log("ReadingSection Debug - All Questions:", {
                totalQuestions: finalSortedQuestions.length,
                questionIds: finalSortedQuestions.map(
                  (q) => q.questionId || q.question_id
                ),
                questionTypes: finalSortedQuestions.map((q) => q.questionType),
                allQuestions: finalSortedQuestions,
                partKey,
                currentPart,
              });

              const output = [];
              let currentInstruction = null;
              const processedQuestions = new Set();

              finalSortedQuestions.forEach((q, idx) => {
                const questionId = q.question_id || q.questionId || q._id;
                const questionType = q.questionType;
                const questionInstruction = getInstructionByType(questionType);
                const questionImage = q.imageUrl || "";

                // Skip if already processed (for grouped questions) - use _id for consistent tracking
                if (processedQuestions.has(q._id)) return;

                // Show instruction block when instruction changes
                if (
                  questionInstruction &&
                  questionInstruction !== currentInstruction
                ) {
                  currentInstruction = questionInstruction;
                  output.push(
                    <Paper
                      key={`instruction-${idx}`}
                      elevation={0}
                      sx={{
                        mb: 3,
                        mt: output.length > 0 ? 3 : 0,
                        p: 2,
                        border: "1px solid #e0e0e0",
                        borderRadius: 1.5,
                        bgcolor: "#fff",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <InfoOutlinedIcon
                          sx={{ fontSize: 28, color: "#9D2235", mr: 1 }}
                        />
                        <Typography
                          variant="h5"
                          sx={{ 
                            fontWeight: 700, 
                            color: "#9D2235",
                            fontSize: `calc((${rightZoom / 100}) * ${window.innerWidth < 600 ? '1.25rem' : '1.5rem'})`,
                          }}
                        >
                          Instruction
                        </Typography>
                      </Box>
                      <HtmlRenderer
                        content={questionInstruction}
                        component="div"
                        sx={{
                          lineHeight: 1.5,
                          userSelect: "text",
                          cursor: "text",
                          fontSize: `calc((${rightZoom / 100}) * ${window.innerWidth < 600 ? '0.875rem' : '0.875rem'})`,
                          whiteSpace: "pre-line",
                        }}
                      />
                      {questionImage && (
                        <Box sx={{ mt: 2 }}>
                          <img 
                            src={questionImage} 
                            alt="Map" 
                            style={{ 
                              maxWidth: "100%", 
                              height: "auto",
                              border: "1px solid #e0e0e0",
                              borderRadius: "8px"
                            }} 
                          />
                        </Box>
                      )}
                    </Paper>
                  );
                }

                // For table_completion and form_completion, group questions with same questionMainHeading
                if (
                  questionType === "table_completion" ||
                  questionType === "form_completion"
                ) {
                  const mainHeading = q.questionMainHeading || "";
                  
                  // Find all questions with same questionMainHeading and type
                  const groupedTableQuestions = finalSortedQuestions.filter((sq) => {
                    const sqType = sq.questionType;
                    const sqHeading = sq.questionMainHeading || "";
                    // Use _id as unique identifier to handle null questionId properly
                    return (
                      (sqType === "table_completion" || sqType === "form_completion") &&
                      sqHeading === mainHeading &&
                      !processedQuestions.has(sq._id)
                    );
                  });

                  // Mark all as processed using _id
                  groupedTableQuestions.forEach((gq) => {
                    processedQuestions.add(gq._id);
                  });

                  // Check if we have table data (for table_completion)
                  const hasTableData = questionType === "table_completion" && q.table;
                  
                  // Create questions map for table rendering
                  const questionsMap = {};
                  groupedTableQuestions.forEach((gq) => {
                    const qid = gq.question_id || gq.questionId;
                    if (qid) {
                      questionsMap[qid] = gq.question || "";
                    }
                  });

                  output.push(
                    <TextHighlighterWrapper
                      key={`group-${questionId}`}
                      onAddNote={onAddNote}
                    >
                      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                        <MockNoteCompletion
                          noteCompletionData={{
                            sections: hasTableData ? [] : [
                              {
                                heading: mainHeading,
                                notes: groupedTableQuestions.map((gq) => ({
                                  questionId: gq.question_id || gq.questionId,
                                  text: gq.question || "",
                                  hasBlank: true,
                                  heading: gq.questionHeading || "",
                                })),
                              },
                            ],
                          }}
                          handleAnswerChange={onAnswerChange}
                          answers={answers}
                          questionMainHeading={mainHeading}
                          tableData={hasTableData ? q.table : null}
                          questionsMap={questionsMap}
                          isFormCompletion={questionType === "form_completion"}
                          zoomLevel={rightZoom}
                        />
                      </Box>
                    </TextHighlighterWrapper>
                  );
                }
                // For notes_completion - group consecutive questions together
                else if (
                  questionType === "notes_completion" ||
                  questionType === "note_completion"
                ) {
                  const mainHeading = q.questionMainHeading || "";
                  
                  console.log("Notes Completion - Before Filter:", {
                    allNotesInFinalSorted: finalSortedQuestions.filter(sq => 
                      sq.questionType === "notes_completion" || sq.questionType === "note_completion"
                    ),
                    mainHeading,
                    processedQuestions: Array.from(processedQuestions)
                  });
                  
                  // Find all consecutive notes_completion questions with same questionMainHeading
                  const groupedNotesQuestions = finalSortedQuestions.filter((sq) => {
                    const sqType = sq.questionType;
                    const sqHeading = sq.questionMainHeading || "";
                    // Use _id as unique identifier to handle null questionId properly
                    const sqUniqueId = sq._id;
                    return (
                      (sqType === "notes_completion" || sqType === "note_completion") &&
                      sqHeading === mainHeading &&
                      !processedQuestions.has(sqUniqueId)
                    );
                  });

                  // Mark all as processed using _id
                  groupedNotesQuestions.forEach((gq) => {
                    processedQuestions.add(gq._id);
                  });

                  console.log("Notes Completion - Grouped Questions:", groupedNotesQuestions.length, groupedNotesQuestions);

                  output.push(
                    <TextHighlighterWrapper
                      key={`group-notes-${questionId || mainHeading || idx}`}
                      onAddNote={onAddNote}
                    >
                      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                        <MockNoteCompletion
                          noteCompletionData={{
                            sections: [
                              {
                                heading: mainHeading,
                                notes: groupedNotesQuestions.map((gq) => {
                                  const hasBlank = (gq.question || "").includes("_");
                                  return {
                                    questionId: gq.question_id || gq.questionId,
                                    text: gq.question || "",
                                    hasBlank: hasBlank,
                                    heading: gq.questionHeading || "",
                                  };
                                }),
                              },
                            ],
                          }}
                          handleAnswerChange={onAnswerChange}
                          answers={answers}
                          questionMainHeading={mainHeading}
                          zoomLevel={rightZoom}
                        />
                      </Box>
                    </TextHighlighterWrapper>
                  );
                }
                // For flowchart_completion - group all with same questionMainHeading into a flowchart layout
                else if (questionType === "flowchart_completion") {
                  const mainHeading = q.questionMainHeading || "";

                  // Collect all flowchart_completion questions with same main heading
                  const groupedFlowchartQuestions = finalSortedQuestions.filter((sq) => {
                    return (
                      sq.questionType === "flowchart_completion" &&
                      (sq.questionMainHeading || "") === mainHeading &&
                      !processedQuestions.has(sq._id)
                    );
                  });

                  groupedFlowchartQuestions.forEach((gq) => processedQuestions.add(gq._id));

                  // Helper: split question text at underscores for blank rendering
                  const splitAtBlanks = (str) => {
                    const parts = str.split(/_{2,}/);
                    return parts.map((p, i) => ({ text: p, blank: i < parts.length - 1 }));
                  };

                  output.push(
                    <TextHighlighterWrapper
                      key={`flowchart-group-${mainHeading || idx}`}
                      onAddNote={onAddNote}
                    >
                      <Box
                        sx={{
                          mt: 2,
                          mb: 3,
                          p: { xs: 2, md: 3 },
                          bgcolor: "#fff",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                        }}
                      >
                        {/* Main heading */}
                        {mainHeading && (
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: "#9D2235",
                              fontSize: `calc((${rightZoom / 100}) * 1rem)`,
                              mb: 2.5,
                              textAlign: "center",
                            }}
                          >
                            {mainHeading}
                          </Typography>
                        )}

                        {/* Flowchart boxes */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          {groupedFlowchartQuestions.map((flowItem, fIdx) => {
                            const flowItemId = flowItem.question_id || flowItem.questionId;
                            const isLabel = flowItemId == null;
                            const prevItem = fIdx > 0 ? groupedFlowchartQuestions[fIdx - 1] : null;
                            const sectionChanged =
                              fIdx === 0 ||
                              (prevItem && (flowItem.questionHeading || "") !== (prevItem.questionHeading || ""));

                            const segments = isLabel ? [] : splitAtBlanks(flowItem.question || "");

                            return (
                              <React.Fragment key={flowItem._id || fIdx}>
                                {/* Section heading label */}
                                {sectionChanged && flowItem.questionHeading && (
                                  <Box
                                    sx={{
                                      mb: 1,
                                      mt: fIdx > 0 ? 2 : 0,
                                      px: 2,
                                      py: 0.5,
                                      bgcolor: "#f0f0f0",
                                      borderRadius: 1,
                                      border: "1px solid #ddd",
                                      alignSelf: "stretch",
                                      textAlign: "center",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: `calc((${rightZoom / 100}) * 0.8rem)`,
                                        fontWeight: 600,
                                        color: "#555",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                      }}
                                    >
                                      {flowItem.questionHeading}
                                    </Typography>
                                  </Box>
                                )}

                                {/* Arrow between consecutive items */}
                                {fIdx > 0 && (
                                  <Box sx={{ display: "flex", justifyContent: "center", my: 1 }}>
                                    <svg
                                      width="40"
                                      height="40"
                                      viewBox="0 0 40 40"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M 20 5 L 20 30"
                                        stroke="#DC143C"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                      />
                                      <path
                                        d="M 20 30 L 15 23 M 20 30 L 25 23"
                                        stroke="#DC143C"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                      />
                                    </svg>
                                  </Box>
                                )}

                                {/* Label box (null questionId) */}
                                {isLabel ? (
                                  <Box
                                    sx={{
                                      width: "100%",
                                      maxWidth: 600,
                                      p: 1.5,
                                      border: "2px solid #9D2235",
                                      borderRadius: 1.5,
                                      bgcolor: "#fff9f9",
                                      textAlign: "center",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: `calc((${rightZoom / 100}) * 0.875rem)`,
                                        color: "#9D2235",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {flowItem.question}
                                    </Typography>
                                  </Box>
                                ) : (
                                  /* Fill-in box (numbered questionId) */
                                  <Box
                                    data-question-id={flowItemId}
                                    sx={{
                                      width: "100%",
                                      maxWidth: 600,
                                      p: 1.5,
                                      border: "1px solid #e0e0e0",
                                      borderRadius: 1.5,
                                      bgcolor: "#fff",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <Typography
                                        component="span"
                                        sx={{
                                          fontWeight: 700,
                                          color: "#9D2235",
                                          fontSize: `calc((${rightZoom / 100}) * 0.875rem)`,
                                          mr: 0.5,
                                        }}
                                      >
                                        {flowItemId}.
                                      </Typography>
                                      {segments.map((seg, sIdx) => (
                                        <React.Fragment key={sIdx}>
                                          {seg.text && (
                                            <Typography
                                              component="span"
                                              sx={{
                                                fontSize: `calc((${rightZoom / 100}) * 0.875rem)`,
                                                lineHeight: 1.6,
                                              }}
                                            >
                                              {seg.text}
                                            </Typography>
                                          )}
                                          {seg.blank && (
                                            <TextField
                                              variant="outlined"
                                              size="small"
                                              value={answers?.[flowItemId] || ""}
                                              onChange={(e) =>
                                                onAnswerChange(flowItemId, e.target.value)
                                              }
                                              autoComplete="off"
                                              sx={{
                                                minWidth: 140,
                                                maxWidth: 200,
                                                "& .MuiOutlinedInput-root": {
                                                  borderRadius: 1.5,
                                                  fontWeight: 600,
                                                  fontSize: `calc((${rightZoom / 100}) * 0.875rem)`,
                                                  "& fieldset": { borderColor: "#ccc" },
                                                  "&:hover fieldset": { borderColor: "#9D2235" },
                                                  "&.Mui-focused fieldset": { borderColor: "#9D2235" },
                                                },
                                                "& input": {
                                                  textAlign: "center",
                                                  color: "#9D2235",
                                                  padding: "6px 8px",
                                                },
                                              }}
                                            />
                                          )}
                                        </React.Fragment>
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </Box>
                      </Box>
                    </TextHighlighterWrapper>
                  );
                }
                // For flowchart_completion_select - dropdown-based flowchart
                else if (questionType === "flowchart_completion_select") {
                  const mainHeading = q.questionMainHeading || "";

                  // Collect all flowchart_completion_select questions with same main heading
                  const groupedFlowchartSelectQuestions = finalSortedQuestions.filter((sq) => {
                    return (
                      sq.questionType === "flowchart_completion_select" &&
                      (sq.questionMainHeading || "") === mainHeading &&
                      !processedQuestions.has(sq._id)
                    );
                  });

                  groupedFlowchartSelectQuestions.forEach((gq) => processedQuestions.add(gq._id));

                  // Get features from the first question's features field
                  const featuresText = groupedFlowchartSelectQuestions[0]?.features || "";
                  const featuresLines = featuresText.split("\n").filter(line => line.trim());
                  const firstLineIsOption = featuresLines.length > 0 && /^[A-Z]\s+/.test(featuresLines[0]);
                  const featuresHeading = firstLineIsOption ? "" : (featuresLines[0] || "");
                  const optionLines = firstLineIsOption ? featuresLines : featuresLines.slice(1);
                  const featureOptions = optionLines.map(line => {
                    const match = line.match(/^([A-Z])\s+(.+)$/);
                    return match ? { label: match[1], text: match[2] } : null;
                  }).filter(Boolean);

                  output.push(
                    <TextHighlighterWrapper
                      key={`flowchart-select-group-${mainHeading || idx}`}
                      onAddNote={onAddNote}
                    >
                      <Box
                        sx={{
                          mt: 2,
                          mb: 3,
                          p: { xs: 2, md: 3 },
                          bgcolor: "#fff",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                        }}
                      >
                        {/* Main heading */}
                        {mainHeading && (
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: "#9D2235",
                              fontSize: `calc((${rightZoom / 100}) * 1rem)`,
                              mb: 2.5,
                              textAlign: "center",
                            }}
                          >
                            {mainHeading}
                          </Typography>
                        )}

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "45fr 55fr" },
                            gap: { xs: 2.5, md: 4 },
                            alignItems: "start",
                          }}
                        >
                          {/* Left side - Features (if available) */}
                          {featureOptions.length > 0 && (
                            <Box
                              sx={{
                                position: { xs: "relative", md: "sticky" },
                                top: { md: 20 },
                                width: "100%",
                                order: 0,
                                bgcolor: "#fafafa",
                                borderRadius: 1.5,
                                p: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                minHeight: "100px",
                              }}
                            >
                              {featuresHeading && (
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 700,
                                    color: "#9D2235",
                                    fontSize: `calc((${rightZoom / 100}) * 0.95rem)`,
                                    mb: 1.5,
                                  }}
                                >
                                  {featuresHeading}
                                </Typography>
                              )}
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                {featureOptions.map((option, optIdx) => (
                                  <Box
                                    key={optIdx}
                                    sx={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        color: "#9D2235",
                                        fontSize: `calc((${rightZoom / 100}) * 0.9rem)`,
                                        minWidth: "24px",
                                      }}
                                    >
                                      {option.label}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: `calc((${rightZoom / 100}) * 0.875rem)`,
                                        lineHeight: 1.5,
                                        color: "#1f2937",
                                      }}
                                    >
                                      {option.text}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Right side - Questions with dropdowns */}
                          <Box sx={{ order: 1 }}>
                            {groupedFlowchartSelectQuestions.map((flowItem, fIdx) => {
                              const flowItemId = flowItem.question_id || flowItem.questionId;
                              return (
                                <React.Fragment key={flowItem._id || fIdx}>
                                  <Box
                                    data-question-id={flowItemId}
                                    sx={{
                                      mb: fIdx < groupedFlowchartSelectQuestions.length - 1 ? 0 : 0,
                                    }}
                                  >
                                    <MockMcq
                                      mcqData={{
                                        questionId: flowItemId,
                                        question: flowItem.question,
                                        options: flowItem.options || [],
                                        questionType: "flowchart_completion_select",
                                      }}
                                      selectedValue={answers[flowItemId] || ""}
                                      handleAnswerChange={(id, v) =>
                                        onAnswerChange(id, v)
                                      }
                                      asDropdown={true}
                                    />
                                  </Box>

                                  {/* Downward arrow between questions */}
                                  {fIdx < groupedFlowchartSelectQuestions.length - 1 && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        my: 1.5,
                                      }}
                                    >
                                      <svg
                                        width="40"
                                        height="40"
                                        viewBox="0 0 40 40"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M 20 5 L 20 30"
                                          stroke="#DC143C"
                                          strokeWidth="3"
                                          strokeLinecap="round"
                                        />
                                        <path
                                          d="M 20 30 L 15 23 M 20 30 L 25 23"
                                          stroke="#DC143C"
                                          strokeWidth="3"
                                          strokeLinecap="round"
                                        />
                                      </svg>
                                    </Box>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </Box>
                        </Box>
                      </Box>
                    </TextHighlighterWrapper>
                  );
                }
                // For diagram_labelling - group by imageUrl, show image once, then text inputs
                else if (questionType === "diagram_labelling") {
                  const imageUrl = q.imageUrl || "";

                  // Collect all diagram_labelling questions with the same imageUrl
                  const groupedDiagramQuestions = finalSortedQuestions.filter((sq) => {
                    return (
                      sq.questionType === "diagram_labelling" &&
                      (sq.imageUrl || "") === imageUrl &&
                      !processedQuestions.has(sq._id)
                    );
                  });

                  groupedDiagramQuestions.forEach((gq) => processedQuestions.add(gq._id));

                  output.push(
                    <TextHighlighterWrapper
                      key={`diagram-group-${questionId}`}
                      onAddNote={onAddNote}
                    >
                      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                        {/* Text input for each question (image already shown in instruction block above) */}
                        <MockNoteCompletion
                          noteCompletionData={{
                            sections: [
                              {
                                heading: "",
                                notes: groupedDiagramQuestions.map((gq) => ({
                                  questionId: gq.question_id || gq.questionId,
                                  text: gq.question || "",
                                  hasBlank: true,
                                })),
                              },
                            ],
                          }}
                          handleAnswerChange={onAnswerChange}
                          answers={answers}
                          zoomLevel={rightZoom}
                        />
                      </Box>
                    </TextHighlighterWrapper>
                  );
                }
                // For other text input questions (individual rendering)
                else if (
                  questionType === "sentence_completion" ||
                  questionType === "short_question_answers" ||
                  questionType === "flow_chart_completion" ||
                  questionType === "diagram_completion"
                ) {
                  processedQuestions.add(q._id);
                  output.push(
                    <TextHighlighterWrapper
                      key={questionId}
                      onAddNote={onAddNote}
                    >
                      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                        <MockNoteCompletion
                          noteCompletionData={{
                            sections: [
                              {
                                heading: "",
                                notes: [
                                  {
                                    questionId: questionId,
                                    text: `${q.question}`,
                                    hasBlank: true,
                                  },
                                ],
                              },
                            ],
                          }}
                          handleAnswerChange={onAnswerChange}
                          answers={answers}
                          zoomLevel={rightZoom}
                        />
                      </Box>
                    </TextHighlighterWrapper>
                  );
                }
                // For summary_completion with options - use dropdown
                else if (questionType === "summary_completion" && q.options && q.options.length > 0) {
                  processedQuestions.add(q._id);
                  
                  // For grouped questions, use first questionId's answer
                  const answerValue = q.isGrouped && q.questionIds?.length > 0
                    ? answers[q.questionIds[0]]
                    : answers[questionId];
                    
                  output.push(
                    <TextHighlighterWrapper
                      key={questionId}
                      onAddNote={onAddNote}
                    >
                      <Box
                        sx={{ mb: { xs: 1.5, sm: 2 } }}
                        data-question-id={questionId}
                      >
                        <MockMcq
                          mcqData={{
                            ...q,
                            question_id: questionId,
                            instructions: questionInstruction,
                          }}
                          selectedValue={answerValue}
                          handleAnswerChange={(qid, value) => {
                            onAnswerChange(qid, value);
                          }}
                          partKey={partKey}
                          questionNumber={q.questionId}
                          asDropdown={true}
                          zoomLevel={rightZoom}
                        />
                      </Box>
                    </TextHighlighterWrapper>
                  );
                }
                // For summary_completion without options - use text input
                else if (questionType === "summary_completion") {
                  processedQuestions.add(q._id);
                  output.push(
                    <TextHighlighterWrapper
                      key={questionId}
                      onAddNote={onAddNote}
                    >
                      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                        <MockNoteCompletion
                          noteCompletionData={{
                            sections: [
                              {
                                heading: "",
                                notes: [
                                  {
                                    questionId: questionId,
                                    text: `${q.question}`,
                                    hasBlank: true,
                                  },
                                ],
                              },
                            ],
                          }}
                          handleAnswerChange={onAnswerChange}
                          answers={answers}
                          zoomLevel={rightZoom}
                        />
                      </Box>
                    </TextHighlighterWrapper>
                  );
                } else {
                  // For MCQ questions (mcq_single, multiple choice, map_labelling, matching_information, etc.)
                  processedQuestions.add(q._id);
                  
                  // For grouped questions, use first questionId's answer
                  const answerValue = q.isGrouped && q.questionIds?.length > 0
                    ? answers[q.questionIds[0]]
                    : answers[questionId];
                    
                  output.push(
                    <TextHighlighterWrapper
                      key={questionId}
                      onAddNote={onAddNote}
                    >
                      <Box
                        sx={{ mb: { xs: 1.5, sm: 2 } }}
                        data-question-id={questionId}
                      >
                        <MockMcq
                          mcqData={{
                            ...q,
                            question_id: questionId,
                            instructions: questionInstruction,
                          }}
                          selectedValue={answerValue}
                          handleAnswerChange={(qid, value) => {
                            onAnswerChange(qid, value);
                          }}
                          partKey={partKey}
                          questionNumber={q.questionId}
                          asDropdown={
                            questionType === "map_labelling" ||
                            questionType === "matching_information" ||
                            questionType === "matching_feature" ||
                            questionType === "matching_headings" ||
                            questionType === "tfng" ||
                            questionType === "true_false_notgiven" ||
                            questionType === "yes_no_notgiven"
                          }
                          zoomLevel={rightZoom}
                        />
                      </Box>
                    </TextHighlighterWrapper>
                  );
                }
              });

              return output;
            })()}
          </TextHighlighterWrapper>
        </Box>
      </Box>{" "}
      {/* FOOTER */}
      <Box
        sx={{
          bgcolor: "#fff",
          borderTop: "1px solid #e0e0e0",
          px: { xs: 1, sm: 4 },
          py: { xs: 1.5, sm: 2 },
          position: "sticky",
          bottom: 0,
          zIndex: 10,
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {showBackButton && (
          <Button
            variant="outlined"
            onClick={handleBackClick}
            sx={{
              minWidth: { xs: 80, sm: 100 },
              textTransform: "none",
              borderColor: "#9D2235",
              color: "#9D2235",
              py: { xs: 0.75, sm: 1 },
              px: { xs: 1.5, sm: 2 },
              borderRadius: 3,
              fontSize: { xs: "0.875rem", sm: "1rem" },
              "&:hover": {
                borderColor: "#9D2235",
                backgroundColor: "rgba(157, 34, 53, 0.04)",
              },
            }}
          >
            Back
          </Button>
        )}
        {!showBackButton && <Box />}
        <Button
          variant="contained"
          size="large"
          onClick={handleNextClick}
          sx={{
            minWidth: { xs: 120, sm: 140 },
            textTransform: "none",
            bgcolor: "#9D2235",
            py: { xs: 0.75, sm: 1 },
            px: { xs: 2, sm: 3 },
            borderRadius: 3,
            fontSize: { xs: "0.875rem", sm: "1rem" },
          }}
        >
          {mode === "practice" && isLastQuestion ? "Finish" : getButtonText()}
        </Button>
      </Box>
      {/* Notes Modal */}
      <NotesModal
        open={notesModalOpen}
        onClose={() => setNotesModalOpen(false)}
        notes={notes}
      />
      {/* TIMER EXPIRED MODAL */}
      <Dialog open={timerExpired}>
        <DialogContent>
          <Typography variant="h6" textAlign="center" gutterBottom>
            Time's up!
          </Typography>
          <Typography textAlign="center" sx={{ mb: 2 }}>
            The test will be submitted in {expiryCountdown} seconds.
          </Typography>
          <Box textAlign="center" sx={{ mb: 2 }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: 48, sm: 72 },
                color: "#9D2235",
              }}
            >
              {expiryCountdown}
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: -1 }}>
              seconds
            </Typography>
          </Box>
          <Box
            textAlign="center"
            sx={{ display: "flex", gap: 2, justifyContent: "center" }}
          >
            {mode === "simulation" && !stayHereUsed && (
              <Button
                variant="outlined"
                onClick={handleStayHere}
                sx={{
                  borderColor: "#9D2235",
                  color: "#9D2235",
                  mt: 1,
                  "&:hover": {
                    borderColor: "#7a1a2a",
                    backgroundColor: "rgba(157, 34, 53, 0.04)",
                  },
                }}
              >
                Stay Here
              </Button>
            )}
            <Button
              variant="contained"
              color="secondary"
              onClick={handleTimerContinue}
              sx={{ bgcolor: "#9D2235", mt: 1 }}
            >
              Continue
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      {/* CONFIRM NAVIGATION MODAL */}
      <Dialog open={showConfirm} onClose={handleConfirmNo}>
        <DialogContent>
          <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Warning color="warning" sx={{ fontSize: 50 }} />
              <Typography variant="h6" fontWeight="bold">
                Unattempted Question – Confirm Section Completion
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 2, pb: 3 }}>
            <Typography variant="body1" paragraph>
              Are you sure you want to move to the next section without
              completing question?
            </Typography>

            <Typography variant="body1" fontWeight="bold">
              Are you sure you want to finish this section?
            </Typography>
          </DialogContent>
          <Box mt={2} display="flex" justifyContent="center" gap={2}>
            <Button variant="outlined" onClick={handleConfirmNo}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmYes}
              sx={{ bgcolor: "#9D2235" }}
            >
              Yes, Continue
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
